import React, { useMemo } from 'react';

import { Collapse, Divider, IconButton } from '@mui/material';

import Label from 'pages/Bulletin/components/BaseScore/components/Label';
import Statistics from 'pages/Bulletin/components/BaseScore/components/Statistics';
import { PARTS_MAP } from 'pages/Bulletin/components/BaseScore/constants';
import { ChartData } from 'pages/Bulletin/types/chart-data';
import { HistoryOfChangesItem } from 'pages/Bulletin/types/history-of-changes';
import ProtectedData, { isProtectedData, LockIcon } from 'pages/components/ProtectedData';
import useCopyToClipboard from 'pages/hooks/use-copy-to-clipboard';

import CopyIcon from './assets/copy.svg';
import CopyDoneIcon from './assets/copy-done.svg';
import useStyles from './styles';

interface Props {
    source?: string | null;
    score: number | string;
    parts?: Record<string, React.ReactNode>;
    vector?: string;
    statistics?: (data: HistoryOfChangesItem[]) => ChartData | null;
    scale: number;
    precision: number;
    open?: boolean;
    onLabelClick?: () => void;
    className?: string;
}

const DataItem: React.FC<Props> = ({
    source,
    score,
    parts,
    vector,
    statistics,
    scale,
    precision,
    open,
    onLabelClick,
    className
}) => {
    const { classes, cx } = useStyles();

    const isProtected = isProtectedData(score);
    const [copy, isCopied] = useCopyToClipboard();
    const labelData = useMemo(() => [{ score }], [score]);

    return (
        <>
            {source ? (
                <Label
                    open={open}
                    onClick={onLabelClick}
                    scale={scale}
                    precision={precision}
                    label={source}
                    data={labelData}
                    className={cx(classes.label, className)}
                />
            ) : null}
            <Collapse in={open} mountOnEnter unmountOnExit classes={{ wrapperInner: classes.collapse }}>
                {parts ? (
                    <div className={cx(classes.parts, className)}>
                        {Object.entries(parts)
                            .filter(([key]) => key in PARTS_MAP)
                            .map(([key, value]) => [PARTS_MAP[key], value])
                            .map(([label, value]) => (
                                <div key={label} className={classes.part}>
                                    <span className={classes.partLabel}>
                                        {label}
                                        {isProtected ? <LockIcon /> : null}
                                    </span>
                                    <span className={classes.partValue}>
                                        <ProtectedData data={value as never} />
                                    </span>
                                </div>
                            ))}
                    </div>
                ) : null}
                {vector ? (
                    <div className={classes.vector}>
                        <span className={classes.vectorValue} title={isProtectedData(vector) ? undefined : vector}>
                            <ProtectedData data={vector} />
                        </span>
                        <Divider flexItem orientation="vertical" />
                        {isProtected ? (
                            <LockIcon />
                        ) : (
                            <IconButton size="small" onClick={() => copy(vector)}>
                                {isCopied ? <CopyDoneIcon /> : <CopyIcon />}
                            </IconButton>
                        )}
                    </div>
                ) : null}
                {statistics ? <Statistics getChart={statistics} scale={scale} /> : null}
            </Collapse>
        </>
    );
};

export default DataItem;
